import React from 'react';
import { Heart, Star, Copy, Check } from 'lucide-react';
import { ColorText_nobg } from '../_helpers';
import { Solar } from '../../services/lunar.js';


// --- Constants ---
const PALACE_NAMES = {
  1: '坎', 2: '坤', 3: '震', 4: '巽', 5: '中',
  6: '乾', 7: '兑', 8: '艮', 9: '离'
};

const GRID_LAYOUT = [4, 9, 2, 3, 5, 7, 8, 1, 6];

const BRANCH_TO_PALACE = {
  '子': 1, '丑': 8, '寅': 8, '卯': 3, '辰': 4, '巳': 4,
  '午': 9, '未': 2, '申': 2, '酉': 7, '戌': 6, '亥': 6
};

// --- Helper Functions ---
const getMaxingText = (result) => {
  if (!result?.maxing) return '无';
  const ma = Object.values(result.maxing).find(v => v);
  return ma || '无';
};

const getHourXunKong = (form) => {
  if (!form || !form.predictYear) return '';
  try {
    const solar = Solar.fromYmdHms(
      parseInt(form.predictYear),
      parseInt(form.predictMonth),
      parseInt(form.predictDay),
      parseInt(form.predictHour),
      parseInt(form.predictMinute) || 0,
      0
    );
    return solar.getLunar().getTimeXunKong();
  } catch (e) {
    console.error("Error calculating Xun Kong:", e);
    return '';
  }
};

// New Helper: Truncate
const truncateText = (text, maxLength) => {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const QimenHeaderSection = ({ result, hourXunKong, predictTimeText }) => (
  <>
    {/* Info Box */}
    <div className="bg-yellow-50 p-3 rounded-lg mb-4 text-sm space-y-2 border border-yellow-100">
      <div className="pb-2 border-b border-yellow-200">
        <span className="text-gray-500">预测时间：</span>
        <span className="font-medium">{predictTimeText}</span>
      </div>

      {/* 第一行：局数、值符、值使 */}
      <div className="flex flex-wrap gap-4 pt-2 border-yellow-200">
        <div><span className="text-gray-500">局数：</span><span className="font-medium">{result.dunju}</span></div>
        <div><span className="text-gray-500">值符：</span><span className="font-medium">{result.zhifu_star} (落{result.zhifu_start_palace}宫)</span></div>
        <div><span className="text-gray-500">值使：</span><span className="font-medium">{result.zhishi_men} (落{result.zhishi_men_place}宫)</span></div>
        <div className="text-gray-500 font-medium">旬首：{result.xunshou}</div>
        <div><span className="text-gray-500">马星：</span><span className="font-medium">{getMaxingText(result)}</span></div>
      </div>

      {/* 第三行：节气信息 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div><span className="text-gray-500">上节气：</span>{result.pre_jieqi} ({result.pre_jieqi_day})</div>
        <div><span className="text-gray-500">下节气：</span>{result.next_jieqi} ({result.next_jieqi_day})</div>
      </div>
    </div>

    {/* Pillars */}
    <div className="text-center mb-6">
      <div className="text-lg sm:text-xl font-bold text-gray-800 tracking-wide flex justify-center gap-4">
        <span><ColorText_nobg text={result.year_pillar} />年</span>
        <span><ColorText_nobg text={result.month_pillar} />月</span>
        <span><ColorText_nobg text={result.day_pillar} />日</span>
        <span><ColorText_nobg text={result.hour_pillar} />时</span>
      </div>
      <div className="mt-2 text-sm text-gray-500">
        (时空：<span className="font-medium text-gray-700">{hourXunKong}</span>)
      </div>
    </div>
  </>
);

const PalaceCell = ({ palaceId, data, hourXunKong }) => {
  const {
    dipan_gan: di = '',
    tianpan_gan: tian = '',
    men = '',
    xing = '',
    shen = '',
    an_gan: an = '',
    maxing: ma = '',
  } = data || {};

  const palaceName = PALACE_NAMES[palaceId] || '';
  const xunKongBranches = hourXunKong ? hourXunKong.split('') : [];
  const currentPalaceBranches = xunKongBranches.filter(b => BRANCH_TO_PALACE[b] === palaceId);

  return (
    <div className="relative border border-gray-300 bg-white p-1 flex flex-col justify-between aspect-square">
      {/* 左上角：旬空 */}
      {currentPalaceBranches.length > 0 && (
        <div className="absolute top-0 left-0 p-0.5 text-[10px] sm:text-xs font-bold z-10">
          {currentPalaceBranches.map(b => (
            <span key={b} className="font-bold sm:text-xs animate-pulse block sm:inline">空:{b}</span>
          ))}
        </div>
      )}

      {/* 左上角：神 */}
      <div className={`text-center text-xs sm:text-sm font-bold mt-1 text-red-600`}>{shen}</div>
      
      {/* 中部：星、门 */}
      <div className="flex justify-between items-center my-auto px-1">
        <div className={`text-xs sm:text-sm font-medium text-blue-600`}>{xing}</div>
        <div className={`text-xs sm:text-sm font-medium text-green-600`}>{men}</div>
      </div>

      {/* 底部：天盘、地盘、暗干 */}
      <div className="flex justify-between items-end text-xs sm:text-sm mb-0.5">
         {/* 左下：暗干 */}
         <div className="text-gray-400 scale-90 origin-bottom-left w-4">
         {an}
         </div>
         
         {/* 中间：天盘/地盘 */}
         <div className="flex flex-col items-center leading-tight">
           <span className="font-bold text-black">
             {tian && tian.replace(/\(.*\)/, '')}
           </span>
           <span className="font-bold text-gray-600">
             {di}
           </span>
         </div>
         
         {/* 右下：宫名/马星 */}
         <div className="flex flex-col items-end w-auto translate-y-2">
            {ma && <span className="text-[10px] font-bold animate-pulse text-indigo-500 scale-90 origin-bottom-right mb-0.5">马:{ma}</span>}
            <span className={`scale-90 origin-bottom-right text-gray-400 whitespace-nowrap`}>
              {palaceName}{palaceId}
            </span>
         </div>
      </div>
      
      {/* 寄宫显示 (如果天盘有括号内容) */}
      {tian && tian.includes('(') && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[10px] text-gray-400 opacity-50 pointer-events-none">
              {tian.match(/\((.*)\)/)[1]}
          </div>
      )}
    </div>
  );
};

const QimenGrid = ({ result, hourXunKong }) => (
  <div className="w-full max-w-full mx-auto aspect-square"> 
    <div className="grid grid-cols-3 gap-0.5 bg-gray-300 border border-gray-300 p-0.5 h-full w-full">
        {GRID_LAYOUT.map(id => (
        <PalaceCell
            key={id}
            palaceId={id}
            data={result.palaces?.[id]}
            hourXunKong={hourXunKong}
        />
        ))}
    </div>
  </div>
);



/**
 * 奇门详情组件
 */
const ExploreDetailQimen = ({
  chartData,
  qimenRef,
  publisherName,
  handleCopy,
  copied,
  handleLike,
  handleFavorite,
  isLiked,
  isFavorited,
  likesCount,
  favoritesCount,
  interactionLoading
}) => {
  // 处理后台返回的排盘结果
  const processedData = React.useMemo(() => {
    if (!chartData) return null;

    // 提取表单数据和计算结果
    const form = {
      name: chartData.name || '',
      gender: chartData.gender || '',
      birthYear: chartData.birthYear,
      birthMonth: chartData.birthMonth,
      birthDay: chartData.birthDay,
      birthHour: chartData.birthHour,
      type: chartData.type || '',
      content: chartData.content || '',
      predictYear: chartData.predictYear,
      predictMonth: chartData.predictMonth,
      predictDay: chartData.predictDay,
      predictHour: chartData.predictHour,
      predictMinute: chartData.predictMinute || 0,
      qimenType: chartData.qimenType || '阳遁',
      qimenMethod: chartData.qimenMethod || '转盘'
    };

    // 提取计算结果
    const result = chartData.result || chartData;

    // 计算时空
    const hourXunKong = getHourXunKong(form);

    return {
      form,
      result,
      hourXunKong
    };
  }, [chartData]);

  if (!processedData) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const { form, result, hourXunKong } = processedData;
  const predictTimeText = `${form.predictYear}年${form.predictMonth}月${form.predictDay}日 ${form.predictHour}时`;

  return (
    <div ref={qimenRef} className="space-y-6">
      {/* 合并的基本信息和奇门排盘 */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden p-4 sm:p-6">
        {/* 基本信息部分 */}
        <div className="mb-4">
          <div className="flex justify-between items-center border-b pb-2 mb-3">
            <h3 className="text-lg font-bold text-gray-900">
              {form.type} <span className="mx-2 text-gray-300">|</span> {truncateText(form.content, 20)}
            </h3>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <p><span className="text-gray-500">姓名：</span>{publisherName || form.name || '-'} {form.gender || ''}</p>
            <p><span className="text-gray-500">生辰：</span>{`${form.birthYear}年${form.birthMonth}月${form.birthDay}日 ${form.birthHour}时`}</p>
          </div>
        </div>

        {/* 奇门排盘部分 */}
        <div>
          
          {/* 排盘头部信息 */}
          <QimenHeaderSection result={result} hourXunKong={hourXunKong} predictTimeText={predictTimeText} />

          {/* 九宫格 */}
          <div className="flex justify-center mb-6">
            <QimenGrid result={result} hourXunKong={hourXunKong} />
          </div>

        </div>
      </div>

      {/* 底部操作：左侧点赞收藏，右侧复制 */}
      <div className="flex items-center justify-between mt-2 py-1">
        <div className="flex items-center gap-4">
          <button
            onClick={handleLike}
            disabled={interactionLoading}
            className={`flex items-center gap-1 transition-all ${
              isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'
            } ${interactionLoading ? 'opacity-50' : ''}`}
            title={isLiked ? '取消点赞' : '点赞'}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
            <span className="text-xs font-medium">{likesCount}</span>
          </button>
          <button
            onClick={handleFavorite}
            disabled={interactionLoading}
            className={`flex items-center gap-1 transition-all ${
              isFavorited ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-400'
            } ${interactionLoading ? 'opacity-50' : ''}`}
            title={isFavorited ? '取消收藏' : '收藏'}
          >
            <Star className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
            <span className="text-xs font-medium">{favoritesCount}</span>
          </button>
        </div>

        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 bg-gray-50 hover:bg-indigo-50 rounded-md transition-colors border border-transparent hover:border-indigo-100"
          title="复制排盘结果为图片"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "已复制到剪贴板" : "复制为图片"}
        </button>
      </div>
    </div>
  );
};

export default ExploreDetailQimen;